import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import z from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (
  amount: number,
  options?: { currency?: string; locale?: string },
): string => {
  const { currency = "USD", locale = "en-US" } = options ?? {};

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatBudget = (
  min?: string | number,
  max?: string | number,
  options?: { currency?: string; locale?: string },
): string => {
  const parseNum = (v?: string | number) => {
    if (v === undefined || v === null || v === "") return null;
    if (typeof v === "number") return isNaN(v) ? null : v;
    const num = parseFloat(String(v).replace(/[^0-9.]/g, ""));
    return isNaN(num) ? null : num;
  };

  const minNum = parseNum(min);
  const maxNum = parseNum(max);

  if (minNum !== null && maxNum !== null) {
    return `${formatCurrency(minNum, options)} - ${formatCurrency(maxNum, options)}`;
  }
  if (minNum !== null) return `From ${formatCurrency(minNum, options)}`;
  if (maxNum !== null) return `Up to ${formatCurrency(maxNum, options)}`;
  return "Flexible";
};

export function capitalizeFirstLetter(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const POPOVER_CONTENT_CLASSNAME =
  "z-20 h-auto max-h-[min(456px,calc(100dvh-140px))] w-[min(var(--radix-popover-trigger-width),calc(100vw-24px))] md:max-h-none overflow-hidden rounded-3xl border bg-white p-0 shadow-2xl";

export type SvcType = "hotel" | "rental" | "tour_act" | "spa_msg";
export const svcTypeSchema = z.enum([
  "hotel",
  "rental",
  "tour_act",
  "spa_msg",
  "any",
]);

/**
 * Builds a query string from a flat params object.
 * - Skips undefined/null values
 * - Trims strings and skips empty-after-trim
 * - Returns "" if nothing to append (no leading "?")
 */
type QueryParamValue =
  string | number | boolean | (string | number)[] | undefined | null;
export const buildQueryString = (
  params: Record<string, QueryParamValue>,
): string => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const str = Array.isArray(value)
      ? value.join(",")
      : typeof value === "string"
        ? value.trim()
        : String(value);
    if (str) query.set(key, str);
  });

  return query.toString();
};

export const FALLBACK_FEATURE_IMAGE =
  "/not-found/no-image-placeholder-gray.jpg";
export const BOY_AVATAR =
  "https://d1z6z2syzgcfjz.cloudfront.net/default/boy.png";
export const GIRL_AVATAR =
  "https://d1z6z2syzgcfjz.cloudfront.net/default/girl.png";

export const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

export const mainPadding = "px-2 md:px-4";

// format to flag
export const formatToflag = (isoCode: string): string =>
  typeof String.fromCodePoint !== "undefined"
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char: string) =>
          String.fromCodePoint(char.charCodeAt(0) + 127397),
        )
    : isoCode;

/**
 * 3242937 => 3,242,937
 * @param value
 * @returns
 */
export const formatNumber = (value: number | string): string => {
  return Number(value || 0).toLocaleString("en-US");
};
